import { useState } from "react";
import { useErrorDisplay } from "../components/ErrorDisplay"
import PositionClient, { PositionErrors } from "../apiClients/PositionClient"
import {Position} from "../models/dtos"
import { useEffect } from "react";
import { useRef } from "react";
import BusySpinner from "../components/BusySpinner";
import { ButtonGroup, Paper, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButtonEx from "../components/IconButtonEx";
import { FormatDateTime, IfTrue } from "../Helpers";
import PositionUpsertPopup from "../popups/PositionUpsertPopup";
import { Securables, UpsertAction } from "../Enums";
import PanelSegment from "../components/PanelSegment";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import { useMainDisplayHeight } from "../components/WindowDimensions";
import { useSearchBox } from "../components/SearchBox";
import OnlyForRole from "../components/OnlyForRole";
import PageTitle from "../components/PageTitle";
import { I18N } from "../I18N";
import MobileEntityCard from "../components/responsive/MobileEntityCard";
import ResponsiveCollection from "../components/responsive/ResponsiveCollection";
import useViewportMode from "../hooks/useViewportMode";

const PositionManagementPage = () => {
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [positions, setPositions] = useState<Position[]>([]);
    const [busy, setBusy] = useState<boolean>(false);
    const initialized = useRef<boolean>(false);
    const [SearchBox, searchFilter] = useSearchBox();
    const { mode: viewportMode } = useViewportMode();
    const mainDisplayHeight = useMainDisplayHeight();

    const loadData = async () => {
        try {
            setBusy(true);
            let p = await new PositionClient().GetAll();
            setPositions(sortByTitle(p));

        } catch (error) {
            setError(error, PositionErrors.GetAll);
        }
        finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (initialized.current)
            return;

        initialized.current = true;
        loadData();
    });

    const handleDelete = async (id: string) => {
        try {
            setBusy(true);
            await new PositionClient().Delete(id);
            await loadData();

        } catch (error) {
            setError(error, PositionErrors.Delete);
        }
        finally {
            setBusy(false);
        }
    };

    const sortByTitle = (list) => list.sort((a,b) => a.Title.localeCompare(b.Title));
    const visiblePositions = searchFilter(positions) as Position[];

    const renderPositionCard = (position: Position) => (
        <MobileEntityCard
            ariaLabel={`Position: ${position.Title}`}
            title={position.Title}
            fields={[{
                label: I18N.Translate('Last Modified'),
                value: FormatDateTime(dayjs(position.LastUpdateDate).toString()),
                fullWidth: true,
            }]}
            actions={
                <>
                    <OnlyForRole role={Securables.position_create}>
                        <PositionUpsertPopup
                            mode={UpsertAction.Update}
                            positionId={position.Id}
                            onChange={() => loadData()}
                            hoverContent={I18N.Translate('Edit Position')}
                            aria-label={`${I18N.Translate('Edit Position')}: ${position.Title}`}
                        />
                    </OnlyForRole>
                    {!position.IsDeleted && (
                        <OnlyForRole role={Securables.position_create}>
                            <IconButtonEx
                                size="small"
                                hoverContent={I18N.Translate('Delete Position')}
                                aria-label={`${I18N.Translate('Delete Position')}: ${position.Title}`}
                                message={I18N.Translate('Are you sure you want to delete this position')}
                                onClick={() => handleDelete(position.Id)}
                            >
                                <DeleteIcon htmlColor="red" fontSize="small" />
                            </IconButtonEx>
                        </OnlyForRole>
                    )}
                </>
            }
        />
    );

    const desktopGrid = (
        <div style={{ height: mainDisplayHeight, width: '100%' }}>
            <DataGridPro
                density="compact"
                rows={visiblePositions.map(x => { return { ...x, id: x.Id, key: x.Id } })}
                columns={[
                    {
                        field: 'Action',
                        headerName: "",
                        sortable: false,
                        filterable: false,
                        resizable: false,
                        width: 70,
                        align: 'center',
                        disableColumnMenu: true,
                        renderCell: ({ row: p }) =>
                            <ButtonGroup>
                                <OnlyForRole role={Securables.position_create}><PositionUpsertPopup mode={UpsertAction.Update} positionId={p.Id} onChange={() => loadData()} /></OnlyForRole>
                            </ButtonGroup>
                    },
                    {
                        field: 'Title',
                        headerName: 'Name',
                        flex: 1
                    },
                    {
                        field: 'LastUpdateDate',
                        headerName: 'Last Modified',
                        flex: 1,
                        renderCell: (params) => FormatDateTime(dayjs(params.value).toString())
                    },
                    {
                        field: 'Delete',
                        headerName: "",
                        sortable: false,
                        filterable: false,
                        resizable: false,
                        disableColumnMenu: true,
                        width: 70,
                        align: 'center',
                        hide: false,
                        renderCell: ({row: p}) => IfTrue(!p.IsDeleted, () => <OnlyForRole role={Securables.position_create}><IconButtonEx size='small' hoverContent='Delete Position' message='Are you sure you want to delete this position' onClick={() => handleDelete(p.Id)}><DeleteIcon htmlColor='red' fontSize='small' /></IconButtonEx></OnlyForRole>)
                    }
                ]}
                components={{
                    Toolbar: GridToolbar
                }}
            />
        </div>
    );

    return (
        <>
            <ErrorDisplay />
            <BusySpinner component={Paper} busy={busy}>
            <PanelSegment 
                titleContent={<PageTitle title={I18N.Translate('Position Management')} />}
                toolbar={<ButtonGroup><OnlyForRole role={Securables.position_create}><PositionUpsertPopup mode={UpsertAction.Create} onAdd={() => loadData()} visibleLabel={I18N.Translate('Add Position')} aria-label={I18N.Translate('Add Position')} /></OnlyForRole><SearchBox /></ButtonGroup>}
                fillHeight>
                    <ResponsiveCollection
                        viewMode={viewportMode}
                        items={visiblePositions}
                        getKey={position => position.Id}
                        renderCard={renderPositionCard}
                        desktop={desktopGrid}
                        ariaLabel={I18N.Translate('Positions')}
                        emptyState={<Typography sx={{ p: 2 }} color="text.secondary">{I18N.Translate('No positions found')}</Typography>}
                    />
                </PanelSegment>
            </BusySpinner>
        </>
    );
}

export default PositionManagementPage;
